import { routes } from "@/config/routes";
import useProfile from "@/hooks/use-profile";
import useStepper from "@/hooks/useStepper";
import { calculateMonthlyPrice } from "@/utilities/functions";
import UserRepository from "@/utilities/repositories/Users";
import { IPackage } from "@/utilities/types/Package";
import { IXeroPayment } from "@/utilities/types/Payment";
import { RequestError } from "@/utilities/types/requests";
import { IVoucher } from "@/utilities/types/Voucher";
import * as React from "react";
import toast from "react-hot-toast";
import { BiCreditCard } from "react-icons/bi";
import { FiCheckCircle } from "react-icons/fi";
import { HiDocumentMagnifyingGlass } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import { Button, Stepper } from "rizzui";
import Logo from "../logo";
import ConnectXeroWrapper from "../xero-payment/connect-xero-wrapper";
import AdminPricingPlansWrapper from "./admin-pricing-plans-wrapper";
import Checkout from "./checkout";
import PaymentVerify from "./payment-verify";
import PricingPlans from "./pricing-plans";
import { IoArrowBack } from "react-icons/io5";
import { IPromotionDTO } from "@/utilities/types/Promotion";

export interface AdminPricingPlansProps {
  pendingPayment?: IXeroPayment | null;
}

const steps = [
  {
    title: "Select Plan",
    description: "Choose your package",
    icon: <HiDocumentMagnifyingGlass className="h-5 w-5" />,
  },
  {
    title: "Payment",
    description: "Complete payment",
    icon: <BiCreditCard className="h-5 w-5" />,
  },
  {
    title: "Verification",
    description: "Verify payment",
    icon: <FiCheckCircle className="h-5 w-5" />,
  },
];

function AdminPricingPlans({ pendingPayment }: AdminPricingPlansProps) {
  const hasPendingPayment = !!pendingPayment;

  const [selectedPlan, setSelectedPlan] = React.useState<IPackage | null>(
    pendingPayment?.package || null
  );

  const [voucherApplied, setVoucherApplied] =
    React.useState<IPromotionDTO | null>(null);

  const { profile, setProfile } = useProfile();
  const navigate = useNavigate();

  // Using useStepper instead of manual state management
  const { currentStep, goToStep, goToNext } = useStepper({
    steps: steps.length,
    initialStep: hasPendingPayment ? 2 : 0,
  });

  const handlePlanSelect = (plan: IPackage) => {
    setSelectedPlan(plan);
    goToNext(); // Use the stepper's goToNext instead of setCurrentStep
  };

  const handlePricingComplete = async () => {
    try {
      //note: skip set status account, auto update from BE
      // await UserRepository.updateMe({
      //   statusAccount: "active",
      // });

      if (profile) {
        setProfile({
          ...profile,
          statusAccount: "active",
        });
      }

      toast.success("Success");
      // navigate(routes.landingPages.create, {
      //   replace: true,
      //   state: { hideBack: true },
      // });
    } catch (error) {
      if (error instanceof RequestError) {
        toast.error(error.message || "Something went wrong");
      }
    }
  };

  const handleCheckoutComplete = (voucher?: IPromotionDTO) => {
    setVoucherApplied(voucher || null);
    goToNext();
  };

  const handlePaymentComplete = () => {
    if (selectedPlan) {
      handlePricingComplete();
    }
  };

  const handleBack = () => {
    goToStep(currentStep - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <PricingPlans onPlanSelect={handlePlanSelect} />;
      case 1:
        return selectedPlan ? (
          <Checkout
            planName={selectedPlan?.name || ""}
            price={selectedPlan.price}
            monthlyPrice={calculateMonthlyPrice(
              selectedPlan.price,
              selectedPlan.duration
            )}
            onBack={handleBack}
            onNext={handleCheckoutComplete}
          />
        ) : null;
      case 2:
        return selectedPlan ? (
          // <ConnectXeroWrapper> // Wrap if need to connect xero
          <PaymentVerify
            typeId={selectedPlan?.id || ""}
            voucher={voucherApplied}
            onPaymentComplete={async () => {
              handlePaymentComplete();
            }}
          />
        ) : // </ConnectXeroWrapper>
        null;

      default:
        return null;
    }
  };

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="my-6 relative  w-full flex justify-center items-center">
        <Button
          variant="outline"
          size="sm"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-fit inline-flex items-center gap-2"
          onClick={() => {
            navigate(-1);
          }}
        >
          <IoArrowBack />
          <span className="max-md:hidden">Back to Dashboard</span>
        </Button>
        <Logo className="max-md:max-w-[120px] md:min-w-[200px] mx-auto" />
      </div>
      <Stepper
        className="mb-6 md:mb-10 [&_.rizzui-step-container]:!justify-center"
        currentIndex={currentStep}
      >
        {steps.map((item, idx) => (
          <Stepper.Step
            key={idx}
            variant="outline"
            active={currentStep === idx}
            completed={currentStep > idx}
            title={<span className="hidden lg:inline">{item.title}</span>}
            description={
              <span className="hidden lg:inline">{item.description}</span>
            }
            icon={item.icon}
            className="text-xs lg:text-sm [&_.rizzui-step-container]:!justify-center"
          />
        ))}
      </Stepper>

      <div className="mb-6 text-center lg:hidden">
        <h2 className="text-xl font-bold">{steps[currentStep]?.title}</h2>
        <p className="text-sm text-gray-500">
          {steps[currentStep]?.description}
        </p>
      </div>

      {renderStep()}
    </div>
  );
}

export default function AdminPricingPlansWithWrapper() {
  return (
    <AdminPricingPlansWrapper>
      {(pendingPayment) => (
        <AdminPricingPlans pendingPayment={pendingPayment} />
      )}
    </AdminPricingPlansWrapper>
  );
}
